import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  BookOpen,
  FileText,
  Calendar,
  GraduationCap,
  ExternalLink,
  Sparkles,
  CheckCircle2,
  Pencil,
  ClipboardList,
} from "lucide-react";
import { toast } from "sonner";
import { EventDetailModal } from "@/components/EventDetailModal";
import { GmailMessageModal } from "@/components/GmailMessageModal";
import { StudyPlanModal } from "@/components/StudyPlanModal";
import { AddHomeAssignmentDialog } from "@/components/AddHomeAssignmentDialog";
import PracticeExamModal from "@/components/PracticeExamModal";
import { formatEventTitle } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";

const fmtDate = (d) => {
  const m = ["jan", "feb", "mar", "apr", "maj", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];
  const wd = ["sön", "mån", "tis", "ons", "tor", "fre", "lör"];
  return `${wd[d.getDay()]} ${d.getDate()} ${m[d.getMonth()]}`;
};

const daysUntil = (iso) => {
  const target = new Date(iso);
  const today = new Date();
  target.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return Math.round((target - today) / 86400000);
};

const quizMessage = (e) => {
  const first = ((e.feed_owner || "").split(" ")[0] || "").trim();
  const isName = /^[A-ZÅÄÖ][a-zåäöé]+$/.test(first) && first !== "Familjen";
  return isName ? `Förhör ${first} på ${e.summary}` : `Förhör mig på ${e.summary}`;
};

export default function HomeworkPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdult = user && !user.is_child;
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [children, setChildren] = useState([]);
  const [addHaOpen, setAddHaOpen] = useState(false);
  const [examEvent, setExamEvent] = useState(null);
  const [addKind, setAddKind] = useState("homework");

  // Breakdown modal state
  const [bdItem, setBdItem] = useState(null);
  const [bdLoading, setBdLoading] = useState(false);
  const [bdSteps, setBdSteps] = useState([]);
  const [bdCommitting, setBdCommitting] = useState(false);

  // Event detail modal (iCal events with AI analysis)
  const [eventModalId, setEventModalId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep-link från pushnotis: /laxor?event=<id> → öppna detaljvyn direkt
  useEffect(() => {
    const evId = searchParams.get("event");
    if (evId) setEventModalId(evId);
  }, [searchParams]);

  const closeEventModal = () => {
    setEventModalId(null);
    if (searchParams.get("event")) {
      searchParams.delete("event");
      setSearchParams(searchParams, { replace: true });
    }
  };
  const [gmailModalId, setGmailModalId] = useState(null);
  const [studyPlanOpen, setStudyPlanOpen] = useState(false);

  // Hemarbete-redigering (direkt från läxor-listan)
  const [editingAsgId, setEditingAsgId] = useState(null);

  // Hämta barn för Hemarbete-dialogen (bara om vuxen)
  useEffect(() => {
    if (!isAdult) return;
    (async () => {
      try {
        const { data } = await api.get("/users");
        setChildren((data || []).filter((m) => m.is_child));
      } catch {}
    })();
  }, [isAdult]);

  const reloadHomework = async () => {
    try {
      const { data } = await api.get("/homework?upcoming=true&limit=200");
      setItems(data || []);
    } catch {}
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [hw, cls, vk, varmdo] = await Promise.all([
          api.get("/homework?upcoming=true&limit=200"),
          api.get("/google/classroom/coursework").catch(() => ({ data: { items: [] } })),
          api.get("/vklass/assignments").catch(() => ({ data: { items: [] } })),
          api.get("/varmdo/classroom/coursework").catch(() => ({ data: { items: [] } })),
        ]);
        const classroomItems = (cls.data?.items || [])
          .filter((c) => c.due && c.state === "PUBLISHED")
          .map((c) => ({
            id: c.id,
            uid: c.id,
            feed_id: "classroom",
            feed_name: c.course_name,
            feed_color: "#3D5A80",
            feed_owner: c.course_name,
            summary: c.title,
            description: c.description || "",
            location: "",
            start: c.due,
            all_day: true,
            category: c.category,
            alternate_link: c.alternate_link,
            source: "classroom",
          }));
        const vklassItems = (vk.data?.items || []).map((v) => ({
          id: v.id,
          uid: v.id,
          feed_id: v.feed_id,
          feed_name: v.course || "Vklass",
          feed_color: "#97BC62",
          feed_owner: v.owner_name || "",
          summary: v.title,
          description: v.description || "",
          location: "",
          start: v.due,
          all_day: true,
          category: /prov|test|tenta/i.test(v.title + " " + (v.description || "")) ? "test" : /läxa|glosor|vocabulary/i.test(v.title + " " + (v.description || "")) ? "homework" : "assignment",
          alternate_link: v.link,
          status: v.status,
          source: "vklass-student",
        }));
        const varmdoItems = (varmdo.data?.items || []).map((v) => ({
          id: `varmdo::${v.id}`,
          uid: v.id,
          feed_id: v.cred_id,
          feed_name: v.course_name || "Värmdö",
          feed_color: v.is_stale ? "#9CA3AF" : "#3D5A80",
          feed_owner: v.owner_label || "",
          summary: v.title,
          description: v.due_text || "",
          location: "",
          // Använd publicerings-datum om vi har det, annars fetched_at
          start: v.published_at || v.fetched_at,
          all_day: true,
          category: /prov|test|tenta/i.test(v.title || "") ? "test" : /läxa|glosor|vocabulary/i.test(v.title || "") ? "homework" : "assignment",
          alternate_link: v.link,
          source: "varmdo-classroom",
          is_stale: !!v.is_stale,
          stale_term: v.stale_term,
        }));
        const merged = [...hw.data, ...classroomItems, ...vklassItems, ...varmdoItems]
          .filter((i) => i.start)
          .sort((a, b) => {
            // Stale items längst ned
            if (a.is_stale && !b.is_stale) return 1;
            if (!a.is_stale && b.is_stale) return -1;
            return (a.start || "").localeCompare(b.start || "");
          });
        setItems(merged);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const openBreakdown = async (item) => {
    setBdItem(item);
    setBdSteps([]);
    setBdLoading(true);
    try {
      const { data } = await api.post("/jarvis/breakdown", {
        title: item.summary,
        description: item.description || "",
        due: item.start,
        commit: false,
      });
      if (data.error) {
        toast.error(data.error);
      }
      setBdSteps(data.steps || []);
    } catch (e) {
      toast.error("Kunde inte bryta ner uppgiften");
    } finally {
      setBdLoading(false);
    }
  };

  const commitBreakdown = async () => {
    if (!bdItem || bdSteps.length === 0) return;
    setBdCommitting(true);
    try {
      const { data } = await api.post("/jarvis/breakdown", {
        title: bdItem.summary,
        description: bdItem.description || "",
        due: bdItem.start,
        commit: true,
        list_type: "personal",
      });
      const n = (data.created || []).length;
      toast.success(`${n} delsteg sparade som att-göra`);
      setBdItem(null);
    } catch (e) {
      toast.error("Kunde inte spara delstegen");
    } finally {
      setBdCommitting(false);
    }
  };

  // Dedup-nyckel: gruppera återkommande hemarbeten (samma home_assignment_id +
  // samma feed_owner) till EN rad. Annars skulle ett dagligt schema ge 200+
  // rader och dölja andra prov/läxor (bug 2026-05-24 — Biologi-provet
  // begravdes bakom 14 dagliga "Läsa egen bok"-rader).
  const dedupKey = (i) => {
    if (i.home_assignment_id) return `ha:${i.home_assignment_id}:${i.feed_owner || ""}`;
    return null;
  };

  const filtered = items.filter((i) => filter === "all" || i.category === filter);

  // Sortera ALLA på start före dedup så vi behåller den NÄSTA kommande
  // instansen av varje återkommande hemarbete (idag/framtiden först).
  const sortedForDedup = [...filtered].sort((a, b) => (a.start || "").localeCompare(b.start || ""));
  const todayIso = new Date().toISOString().slice(0, 10);

  const dedupedMap = new Map();
  const recurrenceMeta = new Map(); // dedupKey → {count, nextStart, lastStart}
  sortedForDedup.forEach((i) => {
    const k = dedupKey(i);
    if (!k) {
      // Unik post — kollar inte mot dedup-map
      dedupedMap.set(i.id, i);
      return;
    }
    const meta = recurrenceMeta.get(k) || { count: 0, nextStart: null, lastStart: null };
    meta.count += 1;
    if (i.start) {
      const dateOnly = i.start.slice(0, 10);
      if (!meta.nextStart && dateOnly >= todayIso) meta.nextStart = i.start;
      meta.lastStart = i.start;
    }
    recurrenceMeta.set(k, meta);
    // Behåll FÖRSTA (= tidigaste/närmast idag) instansen som "primary"
    if (!dedupedMap.has(k)) {
      dedupedMap.set(k, { ...i, _dedup_key: k });
    }
  });
  // Berika med recurrence-metadata
  const deduped = Array.from(dedupedMap.values()).map((i) => {
    if (i._dedup_key) {
      const meta = recurrenceMeta.get(i._dedup_key);
      if (meta && meta.count > 1) {
        return {
          ...i,
          _recurrence_count: meta.count,
          _recurrence_last: meta.lastStart,
        };
      }
    }
    return i;
  });

  const grouped = deduped.reduce((acc, i) => {
    const k = i.feed_owner || i.feed_name || "Allmänt";
    acc[k] = acc[k] || [];
    acc[k].push(i);
    return acc;
  }, {});
  // Sortera barn alfabetiskt; inom varje barn: kommande deadlines först (ascending),
  // utgångna sist
  const groupedEntries = Object.entries(grouped)
    .sort(([a], [b]) => a.localeCompare(b, "sv"))
    .map(([owner, list]) => [
      owner,
      [...list].sort((a, b) => {
        if (a.is_stale && !b.is_stale) return 1;
        if (!a.is_stale && b.is_stale) return -1;
        return (a.start || "").localeCompare(b.start || "");
      }),
    ]);

  return (
    <div className="px-5 pt-3" data-testid="homework-page">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <p className="label-eyebrow">Skolan</p>
          <h1 className="font-display text-2xl font-medium mt-0.5">Läxor & Prov</h1>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isAdult && (
            <>
              <button
                onClick={() => { setAddKind("homework"); setAddHaOpen(true); }}
                className="mt-1 px-3 py-2 rounded-full bg-[color:var(--moss)] text-white text-xs font-medium inline-flex items-center gap-1.5"
                data-testid="open-add-home-assignment"
                title="Lägg till hemarbete för ett eller flera barn"
              >
                + Hemarbete
              </button>
              <button
                onClick={() => { setAddKind("test"); setAddHaOpen(true); }}
                className="mt-1 px-3 py-2 rounded-full bg-[#E07A5F] text-white text-xs font-medium inline-flex items-center gap-1.5"
                data-testid="open-add-test"
                title="Lägg till prov för ett eller flera barn"
              >
                + Prov
              </button>
            </>
          )}
          <button
            onClick={() => setStudyPlanOpen(true)}
            className="mt-1 px-3 py-2 rounded-full bg-[color:var(--jarvis)] text-white text-xs font-medium inline-flex items-center gap-1.5"
            data-testid="open-study-plan"
            title="Be Edit bygga ett studieschema inför kommande prov"
          >
            <Sparkles size={12} /> Studieschema
          </button>
        </div>
      </div>

      <Tabs value={filter} onValueChange={setFilter} className="mb-4">
        <TabsList className="grid grid-cols-4 w-full bg-[color:var(--bg-elevated)]">
          <TabsTrigger value="all" data-testid="filter-all-tab">Alla</TabsTrigger>
          <TabsTrigger value="homework" data-testid="filter-homework-tab">Läxor</TabsTrigger>
          <TabsTrigger value="test" data-testid="filter-test-tab">Prov</TabsTrigger>
          <TabsTrigger value="assignment" data-testid="filter-assignment-tab">Uppgifter</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <p className="text-xs text-[color:var(--text-tertiary)] text-center mt-8">Laddar…</p>
      ) : filtered.length === 0 ? (
        <div className="card-soft p-8 text-center" data-testid="homework-empty">
          <BookOpen size={28} className="mx-auto text-[color:var(--text-tertiary)] mb-3" />
          <p className="text-sm text-[color:var(--text-secondary)]">
            Inga {filter === "test" ? "prov" : filter === "homework" ? "läxor" : filter === "assignment" ? "uppgifter" : "läxor, prov eller uppgifter"} hittade.
          </p>
          <p className="text-xs text-[color:var(--text-tertiary)] mt-2">
            Lägg till barnens skolkalender via inställningar.
          </p>
        </div>
      ) : (
        groupedEntries.map(([owner, list]) => (
          <div key={owner} className="mb-6">
            <h3 className="font-display text-base font-medium mb-2 flex items-center gap-2">
              <span
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: list[0].feed_color }}
              />
              {owner}
              <span className="text-xs font-normal text-[color:var(--text-tertiary)]">
                ({list.length})
              </span>
            </h3>
            <ul className="space-y-2 stagger">
              {list.map((i) => {
                const days = daysUntil(i.start);
                const urgent = days <= 2 && days >= 0;
                const isIcalEvent = !i.source || i.source === "ical";
                return (
                  <li
                    key={i.id}
                    className={`card-soft p-4 fade-up${i.is_stale ? " opacity-60" : ""}${isIcalEvent ? " cursor-pointer hover:bg-[color:var(--bg-elevated)]" : ""}`}
                    data-testid={`hw-item-${i.id}`}
                    onClick={(e) => {
                      // Klick på inre <a>/<button> ska inte trigga modal
                      const tgt = e.target.closest("a, button");
                      if (tgt && tgt !== e.currentTarget) return;
                      if (isIcalEvent) setEventModalId(i.id);
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                          i.category === "test"
                            ? "bg-[#FDF2EE] text-[#E07A5F]"
                            : i.category === "assignment"
                            ? "bg-[#E7F4F1] text-[#2A9D8F]"
                            : "bg-[#F0F6E6] text-[color:var(--moss)]"
                        }`}
                      >
                        {i.category === "test" ? <FileText size={16} /> : i.category === "assignment" ? <ClipboardList size={16} /> : <BookOpen size={16} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                          <span
                            className={`text-[10px] font-semibold uppercase tracking-wider ${
                              i.category === "test" ? "text-[#E07A5F]" : i.category === "assignment" ? "text-[#2A9D8F]" : "text-[color:var(--moss)]"
                            }`}
                          >
                            {i.category === "test" ? "Prov" : i.category === "assignment" ? "Uppgift" : "Läxa"}
                          </span>
                          {i.source === "classroom" && (
                            <span className="text-[10px] flex items-center gap-0.5 text-[color:var(--jarvis)] font-medium">
                              <GraduationCap size={10} /> Classroom
                            </span>
                          )}
                          {i.source === "vklass-student" && (
                            <span className="text-[10px] flex items-center gap-0.5 text-[color:var(--moss)] font-medium">
                              <GraduationCap size={10} /> Vklass · detaljerad
                            </span>
                          )}
                          {i.source === "varmdo-classroom" && (
                            <span className="text-[10px] flex items-center gap-0.5 text-[#3D5A80] font-medium">
                              <GraduationCap size={10} /> Värmdö Classroom
                            </span>
                          )}
                          {i.is_stale && (
                            <span
                              className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#F3F4F6] text-[#6B7280] font-semibold"
                              data-testid={`stale-pill-${i.id}`}
                            >
                              Förfallen {i.stale_term || ""}
                            </span>
                          )}
                          {urgent && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#FEF2F2] text-[#DC2626] font-semibold">
                              {days === 0 ? "Idag" : days === 1 ? "Imorgon" : `Om ${days} dagar`}
                            </span>
                          )}
                          {i._recurrence_count > 1 && (
                            <span
                              className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#EEF4FA] text-[#3D5A80] font-semibold inline-flex items-center gap-0.5"
                              title={`Återkommande t.o.m. ${(i._recurrence_last || "").slice(0,10)}`}
                              data-testid={`recurrence-pill-${i.id}`}
                            >
                              ↻ Återkommande · {i._recurrence_count}×
                            </span>
                          )}
                          {isAdult && i.home_assignment_id && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingAsgId(i.home_assignment_id);
                              }}
                              className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white border border-[color:var(--border)] text-[color:var(--moss)] hover:bg-[color:var(--moss)] hover:text-white text-[11px] font-medium"
                              title="Redigera hemarbete"
                              data-testid={`edit-home-assignment-${i.home_assignment_id}`}
                            >
                              <Pencil size={11} /> Redigera
                            </button>
                          )}
                        </div>
                        <p className="text-sm font-medium leading-snug">{formatEventTitle(i)}</p>
                        <p className="text-xs text-[color:var(--text-secondary)] mt-1 flex items-center gap-1">
                          <Calendar size={11} /> {fmtDate(new Date(i.start))}
                        </p>
                        {i.description && (
                          <p className="text-xs text-[color:var(--text-tertiary)] mt-2 line-clamp-3 whitespace-pre-wrap">
                            {i.description}
                          </p>
                        )}
                        <div className="flex items-center gap-3 mt-2.5 flex-wrap">
                          {i.quizable && (
                            <button
                              type="button"
                              onClick={(ev) => {
                                ev.stopPropagation();
                                navigate(`/?quiz=${encodeURIComponent(quizMessage(i))}`);
                              }}
                              className="text-xs px-2.5 py-1 rounded-full bg-[color:var(--jarvis)] text-white hover:opacity-90 flex items-center gap-1 font-medium transition-opacity"
                              data-testid={`quiz-me-btn-${i.id}`}
                            >
                              <GraduationCap size={11} /> Förhör mig
                            </button>
                          )}
                          {i.exam_material && (
                            <button
                              type="button"
                              onClick={(ev) => { ev.stopPropagation(); setExamEvent(i); }}
                              className="text-xs px-2.5 py-1 rounded-full border border-[color:var(--jarvis)] text-[color:var(--jarvis)] hover:bg-[color:var(--jarvis)] hover:text-white flex items-center gap-1 font-medium transition-colors"
                              data-testid={`practice-exam-btn-${i.id}`}
                            >
                              <FileText size={11} /> Övningsprov
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => openBreakdown(i)}
                            className="text-xs text-[color:var(--jarvis)] hover:underline flex items-center gap-1 font-medium"
                            data-testid={`breakdown-btn-${i.id}`}
                          >
                            <Sparkles size={11} /> Bryt ner med Edit
                          </button>
                          {i.alternate_link && (
                            <a
                              href={i.alternate_link}
                              target="_blank"
                              rel="noreferrer"
                              className="text-xs text-[color:var(--text-secondary)] hover:underline flex items-center gap-1"
                            >
                              Öppna länk <ExternalLink size={10} />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ))
      )}

      <Dialog open={!!bdItem} onOpenChange={(o) => !o && setBdItem(null)}>
        <DialogContent className="max-w-md" data-testid="breakdown-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-base flex items-center gap-2">
              <Sparkles size={16} className="text-[color:var(--jarvis)]" />
              Bryt ner uppgiften
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-[color:var(--text-secondary)]">
              <p className="font-medium text-sm text-[color:var(--text-primary)] mb-1">
                {formatEventTitle(bdItem)}
              </p>
              {bdItem?.start && (
                <p>Deadline: {fmtDate(new Date(bdItem.start))}</p>
              )}
            </div>
            {bdLoading ? (
              <p className="text-sm text-[color:var(--text-tertiary)] py-4 text-center">
                Edit tänker…
              </p>
            ) : bdSteps.length === 0 ? (
              <p className="text-sm text-[color:var(--text-tertiary)] py-2">
                Inga delsteg genererade.
              </p>
            ) : (
              <ol className="space-y-2" data-testid="breakdown-steps">
                {bdSteps.map((s, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-2 text-sm"
                    data-testid={`breakdown-step-${idx}`}
                  >
                    <span className="w-5 h-5 rounded-full bg-[color:var(--bg-elevated)] text-[color:var(--moss)] flex items-center justify-center text-[11px] font-semibold flex-shrink-0 mt-0.5">
                      {idx + 1}
                    </span>
                    <span className="leading-snug">{s}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setBdItem(null)}
              className="rounded-full h-9"
              data-testid="breakdown-cancel"
            >
              Stäng
            </Button>
            <Button
              onClick={commitBreakdown}
              disabled={bdLoading || bdCommitting || bdSteps.length === 0}
              className="rounded-full bg-[color:var(--moss)] hover:bg-[#1f421f] h-9"
              data-testid="breakdown-commit"
            >
              <CheckCircle2 size={14} className="mr-1" />
              {bdCommitting ? "Sparar…" : "Spara som att-göra"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {examEvent && (
        <PracticeExamModal
          eventId={examEvent.id}
          eventTitle={formatEventTitle(examEvent)}
          onClose={() => setExamEvent(null)}
        />
      )}

      {eventModalId && (
        <EventDetailModal
          eventId={eventModalId}
          onClose={closeEventModal}
          onChanged={async () => {
            // Reload list to pick up category-change
            try {
              const [hw] = await Promise.all([api.get("/homework?upcoming=true&limit=200")]);
              // Trigger full reload via simple re-fetch
              setLoading(true);
              const evs = (hw.data || []).map((e) => ({ ...e, source: "ical" }));
              setItems((current) => {
                const others = current.filter((c) => c.source && c.source !== "ical");
                return [...others, ...evs];
              });
              setLoading(false);
            } catch (_) {}
          }}
          onOpenGmail={(id) => setGmailModalId(id)}
        />
      )}
      {gmailModalId && (
        <GmailMessageModal
          messageId={gmailModalId}
          onClose={() => setGmailModalId(null)}
        />
      )}
      {studyPlanOpen && (
        <StudyPlanModal onClose={() => setStudyPlanOpen(false)} />
      )}
      <AddHomeAssignmentDialog
        open={addHaOpen || !!editingAsgId}
        onClose={() => { setAddHaOpen(false); setEditingAsgId(null); }}
        availableChildren={children}
        editAssignmentId={editingAsgId}
        defaultKind={addKind}
        onSaved={(eventIdToOpen) => {
          reloadHomework();
          if (eventIdToOpen) setEventModalId(eventIdToOpen);
        }}
      />
    </div>
  );
}
